import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Spin, Tooltip } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type AppDispatch } from '../../redux/store';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/ToastComponent/store/toastMiddleware';
import ConfirmDialog from './ConfirmDialog';
import {
  addPurity,
  createMetal,
  addType,
  deletePurity,
  deleteType,
  fetchMetals,
  updatePurity,
  updateType,
  type MetalChangeResponse,
  type MetalRecord,
  type Purity,
} from './metalsApi';

const GENERIC_MESSAGE = 'Something went wrong. Please try again.';
const LAST_PURITY_HINT = 'At least one purity is required';
const LAST_TYPE_HINT = 'At least one type is required';

const requiredMark = (
  label: React.ReactNode,
  { required }: { required: boolean }
) => (
  <>
    {label}
    {required && <span className="settings__req">*</span>}
  </>
);

interface PurityValues {
  purityName: string;
  purityPercentage: string;
}

interface TypeValues {
  type: string;
}

// the first purity and type of a metal that is being set up
interface NewMetalValues {
  purityName: string;
  purityPercentage: string;
  type: string;
}

// the metals every organization needs; the page offers to add whichever is missing
const REQUIRED_METALS = ['Gold', 'Silver'];

type PurityDialogState = { metal: MetalRecord; purity?: Purity } | null;
type TypeDialogState = { metal: MetalRecord; type?: string } | null;
type DeleteState =
  | { kind: 'purity'; metal: MetalRecord; purity: Purity }
  | { kind: 'type'; metal: MetalRecord; type: string }
  | null;

// Settings > Metals: Gold and Silver as cards, each with an editable list of purities and of types
export default function MetalsIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [purityForm] = Form.useForm<PurityValues>();
  const [typeForm] = Form.useForm<TypeValues>();

  const [metals, setMetals] = useState<MetalRecord[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  // a popup is saving: its Save button shows a spinner instead of the full-screen loader
  const [isSaving, setIsSaving] = useState(false);
  const [purityDialog, setPurityDialog] = useState<PurityDialogState>(null);
  const [typeDialog, setTypeDialog] = useState<TypeDialogState>(null);
  const [deleting, setDeleting] = useState<DeleteState>(null);
  // set after the first successful load, so "Add Silver" is not offered while the list is still loading
  const [hasLoaded, setHasLoaded] = useState(false);
  // the name of the missing metal being set up ("Gold" / "Silver")
  const [newMetal, setNewMetal] = useState<string | null>(null);
  const [newMetalForm] = Form.useForm<NewMetalValues>();
  // separate searches: one for the purities and one for the types of each metal
  const [searches, setSearches] = useState<Record<string, string>>({});
  const queryOf = (metalId: string, kind: 'purity' | 'type'): string =>
    (searches[`${metalId}:${kind}`] ?? '').trim().toLowerCase();
  const setQuery = (
    metalId: string,
    kind: 'purity' | 'type',
    value: string
  ) => {
    setSearches((current) => ({ ...current, [`${metalId}:${kind}`]: value }));
  };
  const matchesPurity = (purity: Purity, query: string): boolean =>
    query === '' ||
    purity.purityName.toLowerCase().includes(query) ||
    String(purity.purityPercentage).includes(query);
  const matchesType = (type: string, query: string): boolean =>
    query === '' || type.toLowerCase().includes(query);

  // Returns true when the error was handled (401 -> back to sign-in)
  const handleAuthError = (status: number | undefined): boolean => {
    if (status === 401) {
      window.localStorage.clear();
      navigate('/sign-in');
      return true;
    }
    return false;
  };

  const messageOf = (error: any): string => {
    const message = error?.response?.data?.message;
    return typeof message === 'string' ? message : GENERIC_MESSAGE;
  };

  const loadMetals = useCallback(async () => {
    setIsBusy(true);
    try {
      setMetals(await fetchMetals());
      setHasLoaded(true);
    } catch (error: any) {
      const status: number | undefined = error?.response?.status;
      if (status === 401) {
        window.localStorage.clear();
        navigate('/sign-in');
      } else {
        const message = error?.response?.data?.message;
        dispatch(
          showErrorMessage(
            typeof message === 'string' ? message : GENERIC_MESSAGE
          )
        );
      }
    } finally {
      setIsBusy(false);
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    void loadMetals();
  }, [loadMetals]);

  // Every success returns the full updated metal: replace it in the list
  const applyChange = (res: MetalChangeResponse) => {
    setMetals((current) =>
      current.map((metal) => (metal._id === res.metal._id ? res.metal : metal))
    );
    dispatch(showSuccessMessage(res.message ?? 'Updated successfully'));
  };

  // 404 "... not found": the list is out of date, reload it
  const handleFailure = (error: any): void => {
    const status: number | undefined = error?.response?.status;
    if (handleAuthError(status)) return;
    dispatch(showErrorMessage(messageOf(error)));
    if (status === 404) void loadMetals();
  };

  // Joi validation { validation: { body: { keys, message } } } -> under the named inputs
  const showValidation = (
    error: any,
    form: typeof purityForm | typeof typeForm
  ): boolean => {
    const data = error?.response?.data;
    const keys: string[] | undefined = data?.validation?.body?.keys;
    if (!Array.isArray(keys) || keys.length === 0) return false;
    const message: string = data?.validation?.body?.message ?? 'Invalid value';
    (form as any).setFields(
      keys.map((key) => ({ name: key, errors: [message] }))
    );
    return true;
  };

  // ---------------- purities
  const openPurityDialog = (metal: MetalRecord, purity?: Purity) => {
    purityForm.resetFields();
    if (purity !== undefined) {
      purityForm.setFieldsValue({
        purityName: purity.purityName,
        purityPercentage: String(purity.purityPercentage),
      });
    }
    setPurityDialog({ metal, purity });
  };

  const onSavePurity = async (values: PurityValues) => {
    if (purityDialog === null || isSaving) return;
    const { metal, purity } = purityDialog;
    const payload = {
      purityName: values.purityName.trim(),
      purityPercentage: Number(values.purityPercentage),
    };
    setIsSaving(true);
    try {
      const res =
        purity === undefined
          ? await addPurity(metal._id, payload)
          : await updatePurity(metal._id, purity._id, payload);
      applyChange(res);
      setPurityDialog(null);
    } catch (error: any) {
      const data = error?.response?.data;
      if (handleAuthError(error?.response?.status as number | undefined))
        return;
      if (data?.field === 'purityName') {
        // "This purity already exists"
        purityForm.setFields([
          { name: 'purityName', errors: [messageOf(error)] },
        ]);
      } else if (!showValidation(error, purityForm)) {
        handleFailure(error);
        if (error?.response?.status === 404) setPurityDialog(null);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------- types
  const openTypeDialog = (metal: MetalRecord, type?: string) => {
    typeForm.resetFields();
    if (type !== undefined) typeForm.setFieldsValue({ type });
    setTypeDialog({ metal, type });
  };

  const onSaveType = async (values: TypeValues) => {
    if (typeDialog === null || isSaving) return;
    const { metal, type } = typeDialog;
    const next = values.type.trim();
    if (type !== undefined && next === type) {
      typeForm.setFields([
        {
          name: 'type',
          errors: ['Enter a different type from the current one'],
        },
      ]);
      return;
    }
    setIsSaving(true);
    try {
      const res =
        type === undefined
          ? await addType(metal._id, next)
          : await updateType(metal._id, type, next);
      applyChange(res);
      setTypeDialog(null);
    } catch (error: any) {
      const data = error?.response?.data;
      if (handleAuthError(error?.response?.status as number | undefined))
        return;
      if (data?.field === 'type') {
        // "This type already exists"
        typeForm.setFields([{ name: 'type', errors: [messageOf(error)] }]);
      } else if (!showValidation(error, typeForm)) {
        handleFailure(error);
        if (error?.response?.status === 404) setTypeDialog(null);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------- delete
  const onConfirmDelete = async () => {
    const target = deleting;
    setDeleting(null);
    if (target === null || isBusy) return;
    setIsBusy(true);
    try {
      const res =
        target.kind === 'purity'
          ? await deletePurity(target.metal._id, target.purity._id)
          : await deleteType(target.metal._id, target.type);
      applyChange(res);
    } catch (error: any) {
      handleFailure(error);
    } finally {
      setIsBusy(false);
    }
  };

  const missingMetals = hasLoaded
    ? REQUIRED_METALS.filter(
        (name) =>
          !metals.some(
            (metal) => metal.name.toLowerCase() === name.toLowerCase()
          )
      )
    : [];

  const openNewMetal = (name: string) => {
    newMetalForm.resetFields();
    setNewMetal(name);
  };

  const onCreateMetal = async (values: NewMetalValues) => {
    if (newMetal === null || isSaving) return;
    setIsSaving(true);
    try {
      await createMetal({
        name: newMetal,
        price: 0,
        salePrice: 0,
        purityQuality: [
          {
            purityName: values.purityName.trim(),
            purityPercentage: Number(values.purityPercentage),
          },
        ],
        types: [values.type.trim()],
      });
      dispatch(showSuccessMessage(`${newMetal} added`));
      setNewMetal(null);
      setIsSaving(false);
      // reload so the new metal comes with its purity ids, sorted like the others
      await loadMetals();
    } catch (error: any) {
      if (handleAuthError(error?.response?.status as number | undefined))
        return;
      if (!showValidation(error, newMetalForm)) {
        dispatch(showErrorMessage(messageOf(error)));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const deleteDialogMessage = (): React.ReactNode => {
    if (deleting === null) return null;
    return deleting.kind === 'purity' ? (
      <>
        Delete the purity <strong>{deleting.purity.purityName}</strong> (
        {deleting.purity.purityPercentage}%) from {deleting.metal.name}?
      </>
    ) : (
      <>
        Delete the type <strong>{deleting.type}</strong> from{' '}
        {deleting.metal.name}?
      </>
    );
  };

  // A disabled Delete is wrapped in a span so its tooltip still gets the hover events
  const deleteButton = (
    disabled: boolean,
    hint: string,
    onClick: () => void
  ): JSX.Element =>
    disabled ? (
      <Tooltip title={hint}>
        <span className="settings__disabled-wrap">
          <Button
            type="text"
            icon={<DeleteOutlined />}
            className="settings__edit-btn"
            disabled
          >
            Delete
          </Button>
        </span>
      </Tooltip>
    ) : (
      <Button
        type="text"
        icon={<DeleteOutlined />}
        className="settings__edit-btn"
        onClick={onClick}
      >
        Delete
      </Button>
    );

  return (
    <div className="metals">
      <Spin spinning={isBusy} fullscreen />
      <p className="settings__title">Metals</p>
      <p className="settings__subtitle">
        Manage the purities and types of each metal. Prices are set on the
        Overview page.
      </p>
      <p className="metals__note">
        <InfoCircleOutlined />
        <span>
          The purities and types you set here are the options shown when you add
          items in a <strong>Loan</strong>, <strong>Pledge</strong> or{' '}
          <strong>Sale</strong>. Any change here appears in those forms.
        </span>
      </p>

      {metals.length === 0 && missingMetals.length === 0 && !isBusy && (
        <p className="ledger-emails__empty">No metals found.</p>
      )}

      <div className="metals__grid">
        {missingMetals.map((name) => (
          <section
            key={name}
            className="settings__card metal-card metal-card--missing"
          >
            <p className="metal-card__name">
              <TagOutlined /> {name}
            </p>
            <p className="metal-card__missing-text">
              {name} is not set up yet. Add it with its first purity and type,
              then manage the rest here.
            </p>
            <Button
              icon={<PlusOutlined />}
              className="settings__button settings__button--primary"
              onClick={() => {
                openNewMetal(name);
              }}
            >
              Add {name}
            </Button>
          </section>
        ))}
        {metals.map((metal) => {
          const purityQuery = queryOf(metal._id, 'purity');
          const typeQuery = queryOf(metal._id, 'type');
          const purities = metal.purityQuality.filter((p) =>
            matchesPurity(p, purityQuery)
          );
          const types = metal.types.filter((x) => matchesType(x, typeQuery));
          return (
            <section key={metal._id} className="settings__card metal-card">
              <p className="metal-card__name">
                <TagOutlined /> {metal.name}
              </p>

              <div className="metal-card__section">
                <div className="metal-card__head">
                  <p className="metal-card__heading">
                    Purities (
                    {purityQuery === ''
                      ? metal.purityQuality.length
                      : `${purities.length} of ${metal.purityQuality.length}`}
                    )
                  </p>
                  <Button
                    icon={<PlusOutlined />}
                    className="settings__button settings__button--primary metal-card__add"
                    onClick={() => {
                      openPurityDialog(metal);
                    }}
                  >
                    Add purity
                  </Button>
                </div>
                <Input
                  className="settings__input metal-card__search"
                  placeholder="Search purities"
                  prefix={<SearchOutlined />}
                  allowClear
                  value={searches[`${metal._id}:purity`] ?? ''}
                  onChange={(e) => {
                    setQuery(metal._id, 'purity', e.target.value);
                  }}
                />
                <ul className="metal-card__list">
                  {purities.map((purity) => (
                    <li key={purity._id} className="metal-card__row">
                      <span className="metal-card__label">
                        {purity.purityName}
                      </span>
                      <span className="metal-card__percent">
                        {purity.purityPercentage}%
                      </span>
                      <span className="metal-card__actions">
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          className="settings__edit-btn"
                          onClick={() => {
                            openPurityDialog(metal, purity);
                          }}
                        >
                          Edit
                        </Button>
                        {deleteButton(
                          metal.purityQuality.length <= 1,
                          LAST_PURITY_HINT,
                          () => {
                            setDeleting({ kind: 'purity', metal, purity });
                          }
                        )}
                      </span>
                    </li>
                  ))}
                  {purities.length === 0 && (
                    <li className="metal-card__empty">No matching purities</li>
                  )}
                </ul>
              </div>

              <div className="metal-card__section">
                <div className="metal-card__head">
                  <p className="metal-card__heading">
                    Types (
                    {typeQuery === ''
                      ? metal.types.length
                      : `${types.length} of ${metal.types.length}`}
                    )
                  </p>
                  <Button
                    icon={<PlusOutlined />}
                    className="settings__button settings__button--primary metal-card__add"
                    onClick={() => {
                      openTypeDialog(metal);
                    }}
                  >
                    Add type
                  </Button>
                </div>
                <Input
                  className="settings__input metal-card__search"
                  placeholder="Search types"
                  prefix={<SearchOutlined />}
                  allowClear
                  value={searches[`${metal._id}:type`] ?? ''}
                  onChange={(e) => {
                    setQuery(metal._id, 'type', e.target.value);
                  }}
                />
                <ul className="metal-card__list">
                  {types.map((type) => (
                    <li key={type} className="metal-card__row">
                      <span className="metal-card__label">{type}</span>
                      <span className="metal-card__actions">
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          className="settings__edit-btn"
                          onClick={() => {
                            openTypeDialog(metal, type);
                          }}
                        >
                          Edit
                        </Button>
                        {deleteButton(
                          metal.types.length <= 1,
                          LAST_TYPE_HINT,
                          () => {
                            setDeleting({ kind: 'type', metal, type });
                          }
                        )}
                      </span>
                    </li>
                  ))}
                  {types.length === 0 && (
                    <li className="metal-card__empty">No matching types</li>
                  )}
                </ul>
              </div>
            </section>
          );
        })}
      </div>

      <Modal
        open={purityDialog !== null}
        title={
          purityDialog?.purity === undefined
            ? `Add purity to ${purityDialog?.metal.name ?? ''}`
            : `Edit purity of ${purityDialog.metal.name}`
        }
        footer={null}
        onCancel={() => {
          if (!isSaving) setPurityDialog(null);
        }}
        maskClosable={false}
        destroyOnClose
        centered
        getContainer={false}
        width={520}
        className="settings__modal"
      >
        <Form
          form={purityForm}
          layout="vertical"
          requiredMark={requiredMark}
          onFinish={onSavePurity}
        >
          <Form.Item
            label="Purity name"
            name="purityName"
            rules={[
              { required: true, message: 'Please enter the purity name' },
              { whitespace: true, message: 'Please enter the purity name' },
              { max: 40, message: 'Use 40 characters or fewer' },
            ]}
          >
            <Input className="settings__input" placeholder="e.g. 22K" />
          </Form.Item>
          <Form.Item
            label="Percentage"
            name="purityPercentage"
            rules={[
              { required: true, message: 'Please enter the percentage' },
              {
                validator: async (_, value: string | undefined) => {
                  if (value === undefined || value === '') return;
                  const n = Number(value);
                  if (Number.isNaN(n) || n < 0 || n > 100) {
                    throw new Error('Enter a number from 0 to 100');
                  }
                },
              },
            ]}
          >
            <Input
              className="settings__input"
              type="number"
              inputMode="decimal"
              placeholder="e.g. 91.6"
              suffix="%"
              onWheel={(e) => {
                e.currentTarget.blur();
              }}
            />
          </Form.Item>
          <div className="settings__actions">
            <Button
              onClick={() => {
                setPurityDialog(null);
              }}
              disabled={isSaving}
              className="settings__button settings__button--ghost"
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              className={`settings__button settings__button--primary${
                isSaving ? ' is-loading' : ''
              }`}
            >
              {isSaving ? (
                <>
                  <span className="settings__spinner" role="status" />
                  Saving...
                </>
              ) : purityDialog?.purity === undefined ? (
                'Add purity'
              ) : (
                'Save changes'
              )}
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        open={typeDialog !== null}
        title={
          typeDialog?.type === undefined
            ? `Add type to ${typeDialog?.metal.name ?? ''}`
            : `Edit type of ${typeDialog.metal.name}`
        }
        footer={null}
        onCancel={() => {
          if (!isSaving) setTypeDialog(null);
        }}
        maskClosable={false}
        destroyOnClose
        centered
        getContainer={false}
        width={520}
        className="settings__modal"
      >
        <Form
          form={typeForm}
          layout="vertical"
          requiredMark={requiredMark}
          onFinish={onSaveType}
        >
          <Form.Item
            label="Type"
            name="type"
            rules={[
              { required: true, message: 'Please enter the type' },
              { whitespace: true, message: 'Please enter the type' },
              { max: 60, message: 'Use 60 characters or fewer' },
            ]}
          >
            <Input className="settings__input" placeholder="e.g. Chain" />
          </Form.Item>
          <div className="settings__actions">
            <Button
              onClick={() => {
                setTypeDialog(null);
              }}
              disabled={isSaving}
              className="settings__button settings__button--ghost"
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              className={`settings__button settings__button--primary${
                isSaving ? ' is-loading' : ''
              }`}
            >
              {isSaving ? (
                <>
                  <span className="settings__spinner" role="status" />
                  Saving...
                </>
              ) : typeDialog?.type === undefined ? (
                'Add type'
              ) : (
                'Save changes'
              )}
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        open={newMetal !== null}
        title={`Add ${newMetal ?? ''}`}
        footer={null}
        onCancel={() => {
          if (!isSaving) setNewMetal(null);
        }}
        maskClosable={false}
        destroyOnClose
        centered
        getContainer={false}
        width={520}
        className="settings__modal"
      >
        <p className="ledger-emails__dialog-text">
          A metal needs at least one purity and one type. You can add more
          afterwards. The rates are set on the Overview page.
        </p>
        <Form
          form={newMetalForm}
          layout="vertical"
          requiredMark={requiredMark}
          onFinish={onCreateMetal}
        >
          <Form.Item
            label="First purity name"
            name="purityName"
            rules={[
              { required: true, message: 'Please enter the purity name' },
              { whitespace: true, message: 'Please enter the purity name' },
              { max: 40, message: 'Use 40 characters or fewer' },
            ]}
          >
            <Input className="settings__input" placeholder="e.g. 999" />
          </Form.Item>
          <Form.Item
            label="Percentage"
            name="purityPercentage"
            rules={[
              { required: true, message: 'Please enter the percentage' },
              {
                validator: async (_, value: string | undefined) => {
                  if (value === undefined || value === '') return;
                  const n = Number(value);
                  if (Number.isNaN(n) || n < 0 || n > 100) {
                    throw new Error('Enter a number from 0 to 100');
                  }
                },
              },
            ]}
          >
            <Input
              className="settings__input"
              type="number"
              inputMode="decimal"
              placeholder="e.g. 99.9"
              suffix="%"
              onWheel={(e) => {
                e.currentTarget.blur();
              }}
            />
          </Form.Item>
          <Form.Item
            label="First type"
            name="type"
            rules={[
              { required: true, message: 'Please enter the type' },
              { whitespace: true, message: 'Please enter the type' },
              { max: 60, message: 'Use 60 characters or fewer' },
            ]}
          >
            <Input className="settings__input" placeholder="e.g. Coin" />
          </Form.Item>
          <div className="settings__actions">
            <Button
              onClick={() => {
                setNewMetal(null);
              }}
              disabled={isSaving}
              className="settings__button settings__button--ghost"
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              className={`settings__button settings__button--primary${
                isSaving ? ' is-loading' : ''
              }`}
            >
              {isSaving ? (
                <>
                  <span className="settings__spinner" role="status" />
                  Saving...
                </>
              ) : (
                `Add ${newMetal ?? ''}`
              )}
            </Button>
          </div>
        </Form>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title={deleting?.kind === 'purity' ? 'Delete purity' : 'Delete type'}
        message={deleteDialogMessage()}
        confirmText="Delete"
        onCancel={() => {
          setDeleting(null);
        }}
        onConfirm={() => {
          void onConfirmDelete();
        }}
      />
    </div>
  );
}
